import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { usePastorAuth } from "../lib/pastorAuth";
import { Reveal } from "./Reveal";
import { ArchWindow } from "./ArchWindow";
import { GoogleButton } from "./GoogleButton";

const NEEDS = ["New Website", "Website Redesign", "Social Media Marketing", "Online Giving Setup", "Sermon Streaming", "Branding & Logo"];
const initial = { church_name: "", pastor_name: "", email: "", location: "", website: "", member_count: "", needs: [], vision: "" };
const inputCls = "w-full rounded-lg bg-white border border-[#120716]/15 px-4 py-3 text-sm text-[#120716] placeholder:text-[#120716]/35 focus:outline-none focus:border-[#E84C4C] transition-colors";

const SignInCard = ({ login }) => (
  <div className="glass rounded-2xl p-8 md:p-10" data-testid="apply-signin-card">
    <div className="flex items-center gap-3">
      <ShieldCheck className="w-5 h-5 text-[#E84C4C]" />
      <span className="font-orbitron text-[10px] tracking-[0.35em] text-[#E84C4C]">ONE QUICK STEP</span>
    </div>
    <h3 className="font-orbitron font-bold text-xl text-[#120716] mt-4">SIGN IN TO APPLY</h3>
    <p className="mt-4 text-sm text-[#120716]/65 leading-relaxed">
      I ask every church to sign in with Google before applying, for one reason: so you can come back any time and see exactly where your application stands, from received to reviewed to built. No passwords to remember, and I never post or email on your behalf.
    </p>
    <GoogleButton onClick={login} className="mt-8 w-full" testId="apply-google-signin" />
    <p className="mt-4 text-center text-[11px] text-[#120716]/40">Already applied? Signing in takes you straight to your application.</p>
  </div>
);

const ApplyForm = ({ pastor }) => {
  const [form, setForm] = useState({ ...initial, pastor_name: pastor.name || "", email: pastor.email || "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { setForm((f) => ({ ...f, pastor_name: f.pastor_name || pastor.name || "", email: f.email || pastor.email || "" })); }, [pastor]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleNeed = (n) => setForm((f) => ({ ...f, needs: f.needs.includes(n) ? f.needs.filter((x) => x !== n) : [...f.needs, n] }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/applications", form);
      setSubmitted(true);
      toast.success("Received. Luke will read it personally and reply soon.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="glass rounded-2xl p-12 text-center" data-testid="apply-success-message">
        <div className="flex justify-center"><ArchWindow size={40} /></div>
        <h3 className="font-orbitron font-bold text-xl text-[#120716] mt-6">I'VE GOT IT</h3>
        <p className="mt-4 text-[#120716]/70 text-sm leading-relaxed">
          Thank you, {form.pastor_name || "friend"}. Your application for {form.church_name} is on my desk,
          and I'll read it personally before I reply.
          "Commit your work to the Lord, and your plans will be established." — Proverbs 16:3
        </p>
        <Link to="/account" data-testid="apply-track-link" className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#E84C4C] hover:bg-[#f05e5e] text-white text-sm font-semibold px-7 py-3 transition-all">
          Track my application <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="glass rounded-2xl p-8 md:p-10 space-y-5" data-testid="church-application-form">
      <div className="flex items-center justify-between gap-3 text-xs text-[#120716]/55" data-testid="apply-signed-in-as">
        <span>Signed in as <span className="font-semibold text-[#120716]">{pastor.email}</span></span>
        <Link to="/account" className="text-[#E84C4C] font-semibold hover:underline" data-testid="apply-my-applications-link">My applications</Link>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <input required data-testid="apply-church-name" className={inputCls} placeholder="Church name *" value={form.church_name} onChange={set("church_name")} />
        <input required data-testid="apply-pastor-name" className={inputCls} placeholder="Pastor / contact name *" value={form.pastor_name} onChange={set("pastor_name")} />
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <input required type="email" data-testid="apply-email" className={inputCls} placeholder="Email address *" value={form.email} onChange={set("email")} />
        <input required data-testid="apply-location" className={inputCls} placeholder="City, Country *" value={form.location} onChange={set("location")} />
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <input data-testid="apply-website" className={inputCls} placeholder="Current website (if any)" value={form.website} onChange={set("website")} />
        <select data-testid="apply-member-count" className={inputCls} value={form.member_count} onChange={set("member_count")}>
          <option value="">Congregation size</option>
          <option>Under 50</option>
          <option>50 – 200</option>
          <option>200 – 500</option>
          <option>500+</option>
        </select>
      </div>
      <div>
        <p className="text-xs font-orbitron tracking-widest text-[#E84C4C]/80 mb-3">WHAT DO YOU NEED?</p>
        <div className="flex flex-wrap gap-2">
          {NEEDS.map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`apply-need-${n.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => toggleNeed(n)}
              className={`rounded-full px-4 py-2 text-xs font-medium border transition-all duration-300 ${form.needs.includes(n) ? "bg-[#E84C4C] border-[#E84C4C] text-white" : "border-[#120716]/20 text-[#120716]/60 hover:border-[#E84C4C]/60"}`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
      <textarea
        required
        rows={4}
        data-testid="apply-vision"
        className={inputCls}
        placeholder="Tell me your church's story: who you are, what God is doing there, and how a stronger presence online could help... *"
        value={form.vision}
        onChange={set("vision")}
      />
      <button type="submit" disabled={loading} data-testid="apply-submit-btn" className="w-full rounded-full bg-[#E84C4C] hover:bg-[#f05e5e] disabled:opacity-50 text-white font-semibold py-4 transition-all duration-300 hover:shadow-[0_0_32px_rgba(232,76,76,0.5)]">
        {loading ? "Sending..." : "Send It to Luke"}
      </button>
    </form>
  );
};

export const ApplySection = () => {
  const { pastor, loading, login } = usePastorAuth();
  return (
    <section id="apply" className="py-28 md:py-36 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-16">
        <Reveal>
          <div className="flex items-center gap-3">
            <ArchWindow size={14} />
            <span className="font-orbitron text-xs tracking-[0.4em] text-[#E84C4C]">FOR PASTORS</span>
          </div>
          <h2 className="font-orbitron font-bold text-2xl sm:text-3xl lg:text-4xl text-[#120716] mt-4 leading-tight">
            TELL ME ABOUT<br />YOUR CHURCH
          </h2>
          <p className="mt-6 text-[#120716]/70 leading-relaxed text-sm md:text-base max-w-md">
            This is where it starts. Tell me who you are, where you gather, and what you're hoping
            for, and I will read it myself. Not a form processor, not an assistant. If I can help,
            I'll write back within a few days and we'll begin. It will never cost your church a thing.
          </p>
          <p className="mt-8 italic font-light text-[#C13B3B] text-sm border-l-2 border-[#E84C4C] pl-4">
            "And let us not grow weary of doing good, for in due season we will reap,
            if we do not give up." — Galatians 6:9
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          {loading ? (
            <div className="glass rounded-2xl p-10 min-h-[320px] flex items-center justify-center text-xs font-orbitron tracking-widest text-[#E84C4C]" data-testid="apply-loading">LOADING...</div>
          ) : pastor ? (
            <ApplyForm pastor={pastor} />
          ) : (
            <SignInCard login={login} />
          )}
        </Reveal>
      </div>
    </section>
  );
};
